import Image, { type ImageProps } from "next/image";
import { cn } from "@/lib/utils";

// Tiny 1x1 neutral placeholder — decoded instantly by the browser,
// Next.js stretches and blurs it automatically via placeholder="blur".
const PLACEHOLDER_BLUR =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNce+LkfwAH5QLv5JXBYQAAAABJRU5ErkJggg==";

type ImageFit = "cover" | "contain" | "contain-blur";

interface BlurImageProps extends ImageProps {
  wrapperClassName?: string;
  /**
   * How the image sits in its box:
   * - omitted: legacy behavior — caller controls fit via `className`.
   * - "cover": fills the box, cropping overflow (square photo boxes).
   * - "contain": shows the whole image, no crop/stretch (transparent graphics).
   * - "contain-blur": `contain` foreground over a blurred, zoomed copy of the
   *   same image filling the box (opaque photos in non-matching boxes).
   */
  fit?: ImageFit;
}

export const BlurImage = ({
  wrapperClassName,
  className,
  alt,
  placeholder,
  blurDataURL,
  fit,
  ...props
}: BlurImageProps) => {
  const fitClass =
    fit === "cover"
      ? "object-cover"
      : fit === "contain" || fit === "contain-blur"
        ? "object-contain"
        : undefined;

  return (
    <div className={cn("relative overflow-hidden", wrapperClassName)}>
      {fit === "contain-blur" && (
        <Image
          {...props}
          alt=""
          aria-hidden
          priority={false}
          placeholder="empty"
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl brightness-50"
        />
      )}
      <Image
        {...props}
        alt={alt}
        className={cn("relative", fitClass, className)}
        placeholder={placeholder ?? "blur"}
        blurDataURL={blurDataURL ?? PLACEHOLDER_BLUR}
      />
    </div>
  );
};
